import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '../Icon'
import { cn } from '@/lib/utils'

const MIN_DISPLAY_MS = 1800
const FADE_OUT_MS = 500

interface BrandedLoadingScreenProps {
    className?: string
    /** Cover the app as a fixed overlay so page content can load underneath. */
    overlay?: boolean
    /** Signal that the underlying content is ready. Triggers fade-out after the minimum display time. */
    isReady?: boolean
    /** Called after the fade-out animation completes so the parent can unmount the loading screen. */
    onFadeComplete?: () => void
}

/**
 * Full-screen branded loading state with a pulsing Black Crow logo.
 * Used during auth initialization in AuthLayout and OnboardingLayout.
 *
 * When `isReady` + `onFadeComplete` are provided, the component enforces a
 * minimum display time then fades out before calling back.
 */
export const BrandedLoadingScreen: React.FC<BrandedLoadingScreenProps> = ({
    className,
    overlay = false,
    isReady = false,
    onFadeComplete,
}) => {
    const mountTime = useRef(Date.now())
    const [isFading, setIsFading] = useState(false)
    const fadeCompleteCalledRef = useRef(false)
    const onFadeCompleteRef = useRef(onFadeComplete)
    onFadeCompleteRef.current = onFadeComplete

    useEffect(() => {
        if (!isReady || !onFadeCompleteRef.current) return

        const elapsed = Date.now() - mountTime.current
        const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed)

        const timer = setTimeout(() => setIsFading(true), remaining)
        return () => clearTimeout(timer)
    }, [isReady])

    useEffect(() => {
        if (!isFading || !onFadeCompleteRef.current) return

        const fallback = setTimeout(() => {
            if (!fadeCompleteCalledRef.current) {
                fadeCompleteCalledRef.current = true
                onFadeCompleteRef.current?.()
            }
        }, FADE_OUT_MS + 100)
        return () => clearTimeout(fallback)
    }, [isFading])

    const handleTransitionEnd = useCallback(() => {
        if (isFading && !fadeCompleteCalledRef.current) {
            fadeCompleteCalledRef.current = true
            onFadeCompleteRef.current?.()
        }
    }, [isFading])

    return (
        <div
            className={cn(
                'flex min-h-screen w-screen items-center justify-center bg-background text-foreground',
                overlay && 'fixed inset-0 z-[100]',
                className,
            )}
            style={{
                opacity: isFading ? 0 : 1,
                transition: isFading
                    ? `opacity ${FADE_OUT_MS}ms ease-out`
                    : undefined,
            }}
            onTransitionEnd={handleTransitionEnd}
            role="status"
            aria-label="Loading"
        >
            <div className="animate-brand-fade-in">
                <div className="animate-brand-pulse">
                    <Icon
                        type="crow-icon"
                        color="currentColor"
                        width={56}
                        height={56}
                    />
                </div>
            </div>
        </div>
    )
}
